import * as ss from 'superstruct';
import { Id } from '../types';

export const ConnectedAccountStatuses = [
  'active',
  'access_lost',
  'disabled',
] as const;

export const ConnectedAccountStatus = ss.enums(ConnectedAccountStatuses);

export type ConnectedAccountStatus = (typeof ConnectedAccountStatuses)[number];

/**
 * How an account is added: by its ID, reached through our own credentials, or
 * by its own API key.
 */
export type ConnectedAccountIdentity = 'external-id' | 'api-key';

/** A kind of connected account, such as one ad platform's ad accounts. */
export type ConnectedAccountTypeInfo = {
  label: string;
  identity: ConnectedAccountIdentity;
};

export const connectedAccountUsesApiKey = <T extends string>(
  types: Record<T, ConnectedAccountTypeInfo>,
  type: T,
): boolean => types[type].identity === 'api-key';

/**
 * What every account Arrows Up connects to has: an ad account, for one. Each
 * belongs to exactly one Arrows Up account and is checked against its vendor
 * when added; `status` says whether Arrows Up can still reach it.
 */
export const ConnectedAccountResource = ss.type({
  id: Id,
  /**
   * For types that use one. Write-only: reads return `MASKED_VALUE`, and
   * writing that (or nothing) keeps the stored key. A new key is verified
   * before it's saved.
   */
  apiKey: ss.optional(ss.nullable(ss.string())),
  name: ss.optional(ss.nullable(ss.string())),
  status: ss.optional(ConnectedAccountStatus),
  /** Why the account isn't `active`, for people to read. */
  statusDetail: ss.optional(ss.nullable(ss.string())),
  lastVerifiedAt: ss.optional(ss.nullable(ss.date())),
  createdAt: ss.optional(ss.nullable(ss.date())),
  updatedAt: ss.optional(ss.nullable(ss.date())),
});

export type ConnectedAccountResource = ss.Infer<
  typeof ConnectedAccountResource
>;
